import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, RotateCcw, Check } from "lucide-react";
import { patchHistory } from "@/lib/api";
import { toast } from "sonner";
import { log } from "@/lib/log";

const has = (v) => v !== undefined && v !== null;
const fmtPE = (v) => (has(v) ? `${v >= 0 ? "+" : ""}${v.toFixed(2)} D` : "—");
const fmtPower = (v) => (v ? `+${v.toFixed?.(2)} D` : "—");

function NumField({ label, testId, value, onChange, placeholder, step }) {
  return (
    <div>
      <Label className="text-[10px] text-slate-500">{label}</Label>
      <Input
        type="number" step={step} placeholder={placeholder} value={value}
        onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="h-8 text-sm font-mono"
      />
    </div>
  );
}

function PostOpForm({ item: it, onSaved }) {
  const [edit, setEdit] = useState({ od: "", os: "", pod: "", pos: "" });
  const set = (k) => (v) => setEdit((p) => ({ ...p, [k]: v }));

  const save = async () => {
    const payload = {};
    if (edit.od !== "") payload.achieved_refraction_od = parseFloat(edit.od);
    if (edit.os !== "") payload.achieved_refraction_os = parseFloat(edit.os);
    if (edit.pod !== "") payload.implanted_power_od = parseFloat(edit.pod);
    if (edit.pos !== "") payload.implanted_power_os = parseFloat(edit.pos);
    if (payload.achieved_refraction_od === undefined && payload.achieved_refraction_os === undefined) {
      toast.error("Enter at least one refraction value");
      return;
    }
    try {
      await patchHistory(it.id, payload);
      toast.success("Post-op refraction saved");
      setEdit({ od: "", os: "", pod: "", pos: "" });
      onSaved();
    } catch (err) {
      log.error("Failed to save post-op data", err);
      toast.error("Save failed");
    }
  };

  const rxPlaceholder = (v) => (has(v) ? String(v) : "e.g. -0.25");
  const powerPlaceholder = (v, planned) => (has(v) ? String(v) : `plan ${planned ?? "—"}`);
  const saved = has(it.achieved_refraction_od) || has(it.achieved_refraction_os);

  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Post-op achieved refraction (SE)</div>
        {saved && (
          <span className="text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5">Saved</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumField label="OD (D)" step="0.25" testId={`postop-od-${it.id}`} value={edit.od} onChange={set("od")} placeholder={rxPlaceholder(it.achieved_refraction_od)} />
        <NumField label="OS (D)" step="0.25" testId={`postop-os-${it.id}`} value={edit.os} onChange={set("os")} placeholder={rxPlaceholder(it.achieved_refraction_os)} />
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <NumField label="Implanted OD (D)" step="0.5" testId={`implanted-od-${it.id}`} value={edit.pod} onChange={set("pod")}
                  placeholder={powerPlaceholder(it.implanted_power_od, it.result?.od?.iol_power_at_target)} />
        <NumField label="Implanted OS (D)" step="0.5" testId={`implanted-os-${it.id}`} value={edit.pos} onChange={set("pos")}
                  placeholder={powerPlaceholder(it.implanted_power_os, it.result?.os?.iol_power_at_target)} />
      </div>
      <Button size="sm" className="mt-2 w-full bg-sky-700 hover:bg-sky-800 text-white" onClick={save} data-testid={`postop-save-${it.id}`}>
        <Check className="w-3.5 h-3.5 mr-1" /> Save post-op
      </Button>
      {(has(it.prediction_error_od) || has(it.prediction_error_os)) && (
        <div className="mt-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 font-mono">
          PE: OD {fmtPE(it.prediction_error_od)} · OS {fmtPE(it.prediction_error_os)}
        </div>
      )}
    </div>
  );
}

export default function HistoryItem({ item: it, onLoad, onDelete, onSaved }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`history-item-${it.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium truncate">{it.patient_name || "Unknown"}</div>
          <div className="text-xs text-slate-500 font-mono">{new Date(it.created_at).toLocaleString()}</div>
          <div className="text-[11px] text-sky-800 truncate" data-testid={`history-lens-${it.id}`}>
            {it.request?.lens_name || "Unspecified lens"}
          </div>
          <div className="text-xs text-slate-600 mt-1">
            OD: <span className="font-mono">{fmtPower(it.result?.od?.iol_power_emmetropia)}</span>
            {" · "}OS: <span className="font-mono">{fmtPower(it.result?.os?.iol_power_emmetropia)}</span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <Button size="sm" variant="outline" data-testid={`history-load-${it.id}`} onClick={() => onLoad(it)}>
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Load
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(it.id)} className="text-red-600 hover:bg-red-50" data-testid={`history-delete-${it.id}`}>
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
          </Button>
        </div>
      </div>
      <PostOpForm item={it} onSaved={onSaved} />
    </div>
  );
}
